/**
 * Insights — admin-only intelligence hub.
 *
 * Aggregate KPIs + per-user breakdown + activity timeline + most-saved
 * tenders + per-user drill-down drawer. Route-gated via
 * InsightsAccessContext (mirrors backend INSIGHTS_ADMINS allowlist).
 */
import React, { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import {
  Activity, Users, Bookmark, Ban, Sparkles, TrendingUp,
  ChevronRight, RefreshCw, AlertCircle, BarChart3, X,
  ExternalLink,
} from 'lucide-react';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend,
} from 'recharts';
import { format, formatDistanceToNowStrict, parseISO } from 'date-fns';
import { useInsightsAccess } from '../contexts/InsightsAccessContext';
import { insightsAPI } from '../services/insightsAPI';
import './Insights.css';

const Insights = () => {
  const { allowed, loading: accessLoading } = useInsightsAccess();
  const [data, setData] = useState({ overview: null, byUser: [], activity: [], topTenders: [] });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('overview');
  const [drilldown, setDrilldown] = useState(null); // { user_id, user_name }

  const load = async () => {
    setRefreshing(true);
    setError(null);
    try {
      const [overview, byUser, activity, topTenders] = await Promise.all([
        insightsAPI.overview(),
        insightsAPI.byUser(),
        insightsAPI.activity(60),
        insightsAPI.topTenders(20),
      ]);
      setData({ overview, byUser, activity, topTenders });
    } catch (e) {
      console.error('Insights load failed:', e);
      setError(e?.response?.status === 403 ? 'Access denied' : 'Failed to load insights');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (allowed) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed]);

  if (accessLoading) {
    return <div className="insights-page"><div className="insights-loading">Checking access…</div></div>;
  }
  if (!allowed) return <Navigate to="/home" replace />;

  return (
    <div className="insights-page">
      <div className="insights-header">
        <div className="insights-header-title">
          <BarChart3 size={26} className="insights-header-icon" />
          <div>
            <h1>Insights</h1>
            <p>Intelligence hub · platform-wide activity, per-user metrics, drill-down</p>
          </div>
        </div>
        <button className="insights-refresh-btn" onClick={load} disabled={refreshing}>
          <RefreshCw size={14} className={refreshing ? 'spinning' : ''} /> Refresh
        </button>
      </div>

      <div className="insights-tabs">
        {[
          ['overview', 'Overview',      <TrendingUp size={14} key="t" />],
          ['by-user',  'By user',       <Users size={14} key="t" />],
          ['activity', 'Activity feed', <Activity size={14} key="t" />],
          ['top',      'Top tenders',   <Sparkles size={14} key="t" />],
        ].map(([k, label, icon]) => (
          <button
            key={k}
            className={`insights-tab ${tab === k ? 'active' : ''}`}
            onClick={() => setTab(k)}
          >
            {icon} {label}
          </button>
        ))}
      </div>

      {loading && <div className="insights-loading">Loading insights…</div>}
      {error && (
        <div className="insights-error">
          <AlertCircle size={18} /> {error}
        </div>
      )}

      {!loading && !error && tab === 'overview' && <Overview data={data.overview} byUser={data.byUser} onOpenUser={setDrilldown} />}
      {!loading && !error && tab === 'by-user' && <ByUser rows={data.byUser} onOpenUser={setDrilldown} />}
      {!loading && !error && tab === 'activity' && <ActivityFeed events={data.activity} />}
      {!loading && !error && tab === 'top' && <TopTenders rows={data.topTenders} />}

      {drilldown && (
        <UserDrawer user={drilldown} onClose={() => setDrilldown(null)} />
      )}
    </div>
  );
};

// ─────────────── Overview ───────────────

const Overview = ({ data, byUser, onOpenUser }) => {
  if (!data) return null;
  const stats = [
    { label: 'Total bookmarks',     value: data.total_bookmarks,           icon: <Bookmark size={16} />, accent: 'blue' },
    { label: 'Disqualifications',   value: data.total_disqualifications,   icon: <Ban size={16} />,     accent: 'red' },
    { label: 'Analyses complete',   value: data.total_analyses_complete,   icon: <Sparkles size={16} />, accent: 'violet' },
    { label: 'Active users',        value: data.active_users,              icon: <Users size={16} />,    accent: 'amber' },
    { label: 'Saves · last 7 days', value: data.bookmarks_last_7d,         icon: <TrendingUp size={16} />, accent: 'green' },
    { label: 'Saves · last 30 days',value: data.bookmarks_last_30d,        icon: <TrendingUp size={16} />, accent: 'green' },
  ];

  const topUsers = [...byUser]
    .sort((a, b) => (b.bookmarks + b.disqualifications) - (a.bookmarks + a.disqualifications))
    .slice(0, 6);
  const maxTotal = Math.max(1, ...topUsers.map((u) => u.bookmarks + u.disqualifications));

  const series = (data.timeseries_30d || []).map((d) => ({
    ...d,
    day: d.day,
    short: (() => {
      try { return format(parseISO(d.day), 'MMM d'); } catch { return d.day; }
    })(),
  }));

  return (
    <div className="insights-overview">
      <div className="insights-stats-grid">
        {stats.map((s) => (
          <div key={s.label} className={`insights-stat-card accent-${s.accent}`}>
            <div className="insights-stat-icon">{s.icon}</div>
            <div className="insights-stat-label">{s.label}</div>
            <div className="insights-stat-value">{(s.value ?? 0).toLocaleString()}</div>
          </div>
        ))}
      </div>

      <div className="insights-card insights-chart-card">
        <div className="insights-card-head">
          <h3>Activity · last 30 days <span className="insights-card-sub">(daily bookmarks &amp; disqualifications)</span></h3>
        </div>
        <div className="insights-chart-wrap">
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={series} margin={{ top: 6, right: 12, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="gradBookmarks" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"  stopColor="#635bff" stopOpacity={0.45} />
                  <stop offset="100%" stopColor="#635bff" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gradDqs" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"  stopColor="#db2777" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#db2777" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
              <XAxis dataKey="short" tick={{ fontSize: 11, fill: '#6b7280' }} tickLine={false} axisLine={{ stroke: '#e5e7eb' }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#6b7280' }} tickLine={false} axisLine={false} width={28} />
              <Tooltip
                contentStyle={{ border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 12 }}
                labelStyle={{ color: '#0a2540', fontWeight: 600 }}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} iconType="circle" />
              <Area type="monotone" dataKey="bookmarks" name="Bookmarks" stroke="#635bff" strokeWidth={2} fill="url(#gradBookmarks)" />
              <Area type="monotone" dataKey="disqualifications" name="Disqualifications" stroke="#db2777" strokeWidth={2} fill="url(#gradDqs)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="insights-two-col">
        <div className="insights-card">
          <h3>Top organizations <span className="insights-card-sub">(by bookmark count)</span></h3>
          <ul className="insights-bar-list">
            {(data.top_orgs || []).slice(0, 10).map((o) => {
              const maxCount = (data.top_orgs?.[0]?.count) || 1;
              const pct = (o.count / maxCount) * 100;
              return (
                <li key={o.name} className="insights-bar-row">
                  <div className="insights-bar-label" title={o.name}>{o.name || '(blank)'}</div>
                  <div className="insights-bar-track"><div className="insights-bar-fill" style={{ width: `${pct}%` }} /></div>
                  <div className="insights-bar-count">{o.count.toLocaleString()}</div>
                </li>
              );
            })}
            {(!data.top_orgs || data.top_orgs.length === 0) && (
              <li className="insights-empty">No organization data yet.</li>
            )}
          </ul>
        </div>

        <div className="insights-card">
          <h3>Most active users <span className="insights-card-sub">(bookmarks + DQs · click to drill in)</span></h3>
          <ul className="insights-bar-list">
            {topUsers.map((u) => {
              const total = u.bookmarks + u.disqualifications;
              const pct = (total / maxTotal) * 100;
              return (
                <li key={u.user_id} className="insights-bar-row insights-bar-row-clickable" onClick={() => onOpenUser(u)}>
                  <div className="insights-bar-label">{u.user_name || u.user_id}</div>
                  <div className="insights-bar-track">
                    <div className="insights-bar-fill" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="insights-bar-count">{total.toLocaleString()}</div>
                </li>
              );
            })}
            {topUsers.length === 0 && <li className="insights-empty">No user activity yet.</li>}
          </ul>
        </div>
      </div>
    </div>
  );
};

// ─────────────── By user ───────────────

const ByUser = ({ rows, onOpenUser }) => {
  const fmt = (iso) => {
    if (!iso) return '—';
    try { return formatDistanceToNowStrict(new Date(iso), { addSuffix: true }); }
    catch { return iso; }
  };
  return (
    <div className="insights-card">
      <h3>Per-user activity <span className="insights-card-sub">(click a row to see their tenders)</span></h3>
      <div className="insights-table-wrap">
        <table className="insights-table">
          <thead>
            <tr>
              <th>User</th>
              <th className="num">Bookmarks</th>
              <th className="num">Analyses</th>
              <th className="num">Disqualifications</th>
              <th>Last saved</th>
              <th>Last DQ'd</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.user_id} className="insights-row-clickable" onClick={() => onOpenUser(u)}>
                <td>
                  <div className="insights-user-cell">
                    <span className="insights-user-avatar">{(u.user_name || '?').charAt(0).toUpperCase()}</span>
                    <span className="insights-user-name">{u.user_name || u.user_id}</span>
                  </div>
                </td>
                <td className="num">{u.bookmarks?.toLocaleString() ?? 0}</td>
                <td className="num">{u.analyses_complete?.toLocaleString() ?? 0}</td>
                <td className="num">{u.disqualifications?.toLocaleString() ?? 0}</td>
                <td>{fmt(u.last_saved)}</td>
                <td>{fmt(u.last_disqualified)}</td>
                <td><ChevronRight size={14} className="insights-row-chev" /></td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={7} className="insights-empty">No user activity yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─────────────── User drill-down drawer ───────────────

const UserDrawer = ({ user, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [sub, setSub] = useState('bookmarks'); // bookmarks | disqualifications | analyses

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true); setErr(null);
      try {
        const r = await insightsAPI.userTenders(user.user_id);
        if (!cancelled) setData(r);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || 'Failed to load user tenders');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [user.user_id]);

  // ESC closes
  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  const counts = data?.counts || { bookmarks: 0, disqualifications: 0, analyses: 0 };
  const list = data?.[sub] || [];

  return (
    <div className="insights-drawer-backdrop" onClick={onClose}>
      <aside className="insights-drawer" onClick={(e) => e.stopPropagation()}>
        <header className="insights-drawer-head">
          <div className="insights-user-cell">
            <span className="insights-user-avatar lg">{(user.user_name || '?').charAt(0).toUpperCase()}</span>
            <div>
              <div className="insights-drawer-name">{user.user_name || user.user_id}</div>
              <div className="insights-drawer-id">{user.user_id}</div>
            </div>
          </div>
          <button className="insights-drawer-close" onClick={onClose} aria-label="Close"><X size={18} /></button>
        </header>

        <div className="insights-drawer-tabs">
          {[
            ['bookmarks',        'Bookmarks',         counts.bookmarks,         <Bookmark size={13} key="i" />],
            ['analyses',         'Analyses',          counts.analyses,          <Sparkles size={13} key="i" />],
            ['disqualifications','Disqualifications', counts.disqualifications, <Ban size={13} key="i" />],
          ].map(([k, label, n, icon]) => (
            <button
              key={k}
              className={`insights-drawer-tab ${sub === k ? 'active' : ''}`}
              onClick={() => setSub(k)}
            >
              {icon} {label} <span className="insights-drawer-tab-count">{n}</span>
            </button>
          ))}
        </div>

        <div className="insights-drawer-body">
          {loading && <div className="insights-loading">Loading…</div>}
          {err && <div className="insights-error"><AlertCircle size={16} /> {err}</div>}
          {!loading && !err && list.length === 0 && (
            <div className="insights-empty">No {sub} for this user.</div>
          )}
          {!loading && !err && list.length > 0 && (
            <ul className="insights-tender-list">
              {list.map((t) => (
                <li key={`${sub}-${t.tender_id}-${t.bookmarked_at || t.disqualified_at}`} className="insights-tender-row">
                  <div className="insights-tender-main">
                    <Link
                      to={`/tender/${encodeURIComponent(t.tender_id)}`}
                      className="insights-tender-title"
                      onClick={onClose}
                    >
                      {t.title || t.tender_id} <ExternalLink size={11} className="insights-tender-arrow" />
                    </Link>
                    <div className="insights-tender-meta">
                      {t.organization && <span>{t.organization}</span>}
                      {t.closing_date && <span>Closes {t.closing_date}</span>}
                      {t.status && <span className="insights-tender-pill">{t.status}</span>}
                      {sub === 'bookmarks' && t.pipeline_stage && (
                        <span className="insights-tender-pill stage">{t.pipeline_stage.replace(/_/g, ' ')}</span>
                      )}
                      {sub === 'analyses' && t.bid_recommendation && (
                        <span className={`insights-tender-pill rec-${(t.bid_recommendation || '').toLowerCase()}`}>
                          {t.bid_recommendation}
                        </span>
                      )}
                      {sub === 'disqualifications' && t.reason && (
                        <span className="insights-activity-reason">"{t.reason}"</span>
                      )}
                    </div>
                  </div>
                  <div className="insights-tender-ts">
                    {(() => {
                      const iso = t.bookmarked_at || t.analyzed_at || t.disqualified_at;
                      if (!iso) return '';
                      try { return formatDistanceToNowStrict(new Date(iso), { addSuffix: true }); }
                      catch { return iso; }
                    })()}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </aside>
    </div>
  );
};

// ─────────────── Activity feed ───────────────

const ActivityFeed = ({ events }) => {
  const fmt = (iso) => {
    if (!iso) return '—';
    try {
      return `${format(new Date(iso), 'MMM d, yyyy · HH:mm')} (${formatDistanceToNowStrict(new Date(iso), { addSuffix: true })})`;
    } catch { return iso; }
  };
  return (
    <div className="insights-card">
      <h3>Recent activity <span className="insights-card-sub">({events.length} events)</span></h3>
      <ul className="insights-activity-list">
        {events.map((e, i) => (
          <li key={`${e.type}-${e.tender_id}-${i}`} className={`insights-activity-item type-${e.type}`}>
            <div className={`insights-activity-icon type-${e.type}`}>
              {e.type === 'bookmark' ? <Bookmark size={13} /> : <Ban size={13} />}
            </div>
            <div className="insights-activity-main">
              <div className="insights-activity-line">
                <strong>{e.user_name || e.user_id}</strong>
                {' '}
                {e.type === 'bookmark' ? 'saved' : 'disqualified'}
                {' '}
                <Link to={`/tender/${encodeURIComponent(e.tender_id)}`} className="insights-activity-tender">
                  {e.title || e.tender_id}
                </Link>
              </div>
              <div className="insights-activity-meta">
                {e.organization && <span>{e.organization}</span>}
                <span>{fmt(e.ts)}</span>
                {e.reason && <span className="insights-activity-reason">"{e.reason}"</span>}
              </div>
            </div>
          </li>
        ))}
        {events.length === 0 && <li className="insights-empty">No activity yet.</li>}
      </ul>
    </div>
  );
};

// ─────────────── Top tenders ───────────────

const TopTenders = ({ rows }) => (
  <div className="insights-card">
    <h3>Most-saved tenders <span className="insights-card-sub">(by distinct users)</span></h3>
    <div className="insights-table-wrap">
      <table className="insights-table">
        <thead>
          <tr>
            <th>Tender</th>
            <th>Organization</th>
            <th className="num">Saved by</th>
            <th className="num">Total saves</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((t) => (
            <tr key={t.tender_id}>
              <td className="cell-title">{t.title || t.tender_id}</td>
              <td>{t.organization || '—'}</td>
              <td className="num">{t.saved_by_count?.toLocaleString() ?? 0}</td>
              <td className="num">{t.saved_events?.toLocaleString() ?? 0}</td>
              <td>
                <Link to={`/tender/${encodeURIComponent(t.tender_id)}`} className="insights-drill">
                  Open <ChevronRight size={13} />
                </Link>
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={5} className="insights-empty">No saved tenders yet.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  </div>
);

export default Insights;
